import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  FileText, 
  Download,
  Star,
  Building,
  MapPin,
  Globe,
  Award,
  Clock,
  DollarSign,
  Shield,
  AlertTriangle,
  MessageCircle
} from 'lucide-react';

const ApplicationReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState({
    score: '',
    notes: '',
    status: '',
    feedback: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Mock application data
  const mockApplication = {
    id: parseInt(id),
    companyName: "EcoVerify International",
    applicantName: "Dr. Sarah Mitchell",
    email: "sarah@ecoverify.com",
    phone: "+1-555-123-4567",
    status: "pending",
    submittedDate: "2024-01-20T10:30:00Z",
    
    // Company Information
    companyType: "Private Corporation",
    registrationNumber: "EV-2012-001",
    foundedYear: 2012,
    employeeCount: "50-100",
    website: "https://ecoverify.com",
    
    // Contact & Location
    headquarters: {
      country: "United States",
      city: "San Francisco",
      address: "123 Green Street, Suite 400",
      zipCode: "94111"
    },
    
    // Experience & Credentials
    yearsOfExperience: 12,
    totalProjectsVerified: 150,
    accreditations: [
      "ISO 14065:2020 Accreditation",
      "ANSI National Accreditation Board",
      "International Association of Environmental Auditors"
    ],
    certifications: [
      "Certified Carbon Verifier (CCV)",
      "Environmental Management Systems Lead Auditor",
      "Greenhouse Gas Verification Professional"
    ],
    
    // Capabilities
    supportedStandards: ["Gold Standard", "VCS", "CDM", "ACR"],
    verificationTypes: [
      "Forest Conservation", "Renewable Energy", "Energy Efficiency", 
      "Methane Capture", "Blue Carbon"
    ],
    geographicCoverage: ["North America", "Europe", "Asia"],
    technicalCapabilities: [
      "Remote Sensing & GIS", "Field Surveys", "Laboratory Analysis",
      "Drone Technology", "Satellite Monitoring", "IoT Sensors"
    ],
    
    // Team & Infrastructure
    teamSize: 45,
    seniorExperts: 12,
    qualityAssurance: "ISO 9001:2015 certified quality management system",
    
    // Pricing
    pricingModel: "Project-based with milestone payments",
    standardRates: {
      forestry: 0.45,
      renewable: 0.25,
      efficiency: 0.35,
      other: 0.40
    },
    
    // References
    clientReferences: [
      {
        name: "Green Energy Corp",
        contact: "John Smith - john@greenenergy.com",
        projectType: "Solar Farm Verification",
        relationship: "3+ years"
      },
      {
        name: "Forest Conservation Trust",
        contact: "Maria Garcia - maria@foresttrust.org",
        projectType: "REDD+ Project Verification",
        relationship: "5+ years"
      }
    ],
    
    // Documents
    documents: [
      { name: "Company Registration", status: "verified", type: "legal" },
      { name: "ISO 14065 Certificate", status: "verified", type: "accreditation" },
      { name: "Insurance Policy", status: "verified", type: "insurance" },
      { name: "Sample Verification Report 1", status: "pending", type: "sample" },
      { name: "Sample Verification Report 2", status: "pending", type: "sample" },
      { name: "Team CVs", status: "verified", type: "personnel" }
    ],
    
    // Assessment flags
    flags: {
      documentsComplete: true,
      referencesVerified: false,
      experienceAdequate: true,
      geographicFit: true,
      pricingCompetitive: true
    }
  };

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setApplication(mockApplication);
      setLoading(false);
    }, 1000);
  }, [id]);

  const handleSubmitReview = async (decision) => {
    setSubmitting(true);
    
    try {
      const reviewPayload = {
        applicationId: id,
        decision,
        score: parseFloat(reviewData.score),
        notes: reviewData.notes,
        feedback: reviewData.feedback,
        reviewedBy: "Admin User", // Replace with actual admin info
        reviewedAt: new Date().toISOString()
      };

      console.log('Submitting review:', reviewPayload);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert(`Application ${decision} successfully!`);
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Error submitting review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getDocumentStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const calculateOverallScore = () => {
    const flags = application?.flags || {};
    let score = 0;
    let total = 0;

    Object.values(flags).forEach(flag => {
      if (flag === true) score++;
      total++;
    });

    return total > 0 ? ((score / total) * 10).toFixed(1) : 0;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Application Not Found</h2>
        <Link to="/admin/dashboard" className="text-green-600 hover:text-green-700">
          Return to Admin Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link
            to="/admin/dashboard"
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Application Review</h1>
            <p className="text-gray-600">{application.companyName}</p>
          </div>
        </div>

        {/* Status and Quick Actions */}
        <div className="flex justify-between items-center bg-white rounded-lg p-4 shadow-sm border">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">Status:</span>
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 capitalize">
              {application.status.replace('_', ' ')}
            </span>
            <span className="text-sm text-gray-600">Submitted:</span>
            <span className="text-sm font-medium">{new Date(application.submittedDate).toLocaleDateString()}</span>
          </div>
          <div className="flex space-x-2">
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center">
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Company Overview */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Building className="h-5 w-5 mr-2" />
              Company Overview
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Basic Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Company Type:</span>
                    <span className="font-medium">{application.companyType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Registration:</span>
                    <span className="font-medium">{application.registrationNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Founded:</span>
                    <span className="font-medium">{application.foundedYear}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Employees:</span>
                    <span className="font-medium">{application.employeeCount}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-3">Contact Information</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">Primary Contact:</span>
                    <p className="font-medium">{application.applicantName}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="font-medium">{application.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <p className="font-medium">{application.phone}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Website:</span>
                    <a href={application.website} className="font-medium text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                      {application.website}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                Headquarters
              </h3>
              <p className="text-sm text-gray-700">
                {application.headquarters.address}, {application.headquarters.city}, {application.headquarters.country} {application.headquarters.zipCode}
              </p>
            </div>
          </div>

          {/* Experience & Credentials */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Award className="h-5 w-5 mr-2" />
              Experience & Credentials
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Track Record</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Years of Experience:</span>
                    <span className="font-medium">{application.yearsOfExperience} years</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Projects Verified:</span>
                    <span className="font-medium">{application.totalProjectsVerified}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Team Size:</span>
                    <span className="font-medium">{application.teamSize} members</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Senior Experts:</span>
                    <span className="font-medium">{application.seniorExperts}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-3">Geographic Coverage</h3>
                <div className="flex flex-wrap gap-2">
                  {application.geographicCoverage.map((region, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {region}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3">Accreditations</h3>
              <ul className="space-y-1">
                {application.accreditations.map((accreditation, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                    {accreditation}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3">Certifications</h3>
              <ul className="space-y-1">
                {application.certifications.map((certification, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-center">
                    <Shield className="h-4 w-4 text-blue-600 mr-2" />
                    {certification}
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Supported Standards</h3>
                <div className="flex flex-wrap gap-2">
                  {application.supportedStandards.map((standard, index) => (
                    <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      {standard}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-3">Verification Types</h3>
                <div className="flex flex-wrap gap-2">
                  {application.verificationTypes.map((type, index) => (
                    <span key={index} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Technical Capabilities */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Globe className="h-5 w-5 mr-2" />
              Technical Capabilities
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {application.technicalCapabilities.map((capability, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                  {capability}
                </div>
              ))}
            </div>

            <div className="mt-6">
              <h3 className="font-medium text-gray-900 mb-2">Quality Assurance</h3>
              <p className="text-sm text-gray-700">{application.qualityAssurance}</p>
            </div>
          </div>

          {/* Pricing Structure */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Pricing Structure
            </h2>

            <div className="mb-4">
              <h3 className="font-medium text-gray-900 mb-2">Pricing Model</h3>
              <p className="text-sm text-gray-700">{application.pricingModel}</p>
            </div>

            <div>
              <h3 className="font-medium text-gray-900 mb-3">Standard Rates (USD per credit)</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Forestry</p>
                  <p className="text-lg font-semibold text-gray-900">${application.standardRates.forestry}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Renewable</p>
                  <p className="text-lg font-semibold text-gray-900">${application.standardRates.renewable}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Efficiency</p>
                  <p className="text-lg font-semibold text-gray-900">${application.standardRates.efficiency}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">Other</p>
                  <p className="text-lg font-semibold text-gray-900">${application.standardRates.other}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Client References */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <MessageCircle className="h-5 w-5 mr-2" />
              Client References
            </h2>

            <div className="space-y-4">
              {application.clientReferences.map((reference, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-900">{reference.name}</h3>
                    <span className="text-xs text-gray-500">{reference.relationship}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{reference.contact}</p>
                  <p className="text-sm text-gray-700">{reference.projectType}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Assessment Summary */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Assessment Summary</h3>
            
            <div className="text-center mb-6">
              <div className="text-3xl font-bold text-gray-900 mb-1">
                {calculateOverallScore()}/10
              </div>
              <p className="text-sm text-gray-600">Overall Score</p>
            </div>

            <div className="space-y-3">
              {Object.entries(application.flags).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                  </span>
                  {value ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Documents Status */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents Status</h3>
            
            <div className="space-y-3">
              {application.documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{doc.type}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDocumentStatusColor(doc.status)}`}>
                      {doc.status}
                    </span>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Download className="h-3 w-3 text-gray-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Review Form */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit Review</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Score (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="0.1"
                  value={reviewData.score}
                  onChange={(e) => setReviewData(prev => ({ ...prev, score: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="8.5"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Notes
                </label>
                <textarea
                  value={reviewData.notes}
                  onChange={(e) => setReviewData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Internal review notes..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Feedback to Applicant
                </label>
                <textarea
                  value={reviewData.feedback}
                  onChange={(e) => setReviewData(prev => ({ ...prev, feedback: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Feedback that will be sent to the applicant..."
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => handleSubmitReview('approved')}
                  disabled={submitting}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition duration-200 flex items-center justify-center"
                >
                  {submitting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleSubmitReview('rejected')}
                  disabled={submitting}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition duration-200 flex items-center justify-center"
                >
                  {submitting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationReview; 